"use client";

import { Shimmer } from "@/components/ai-elements/shimmer";
import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "lucide-react";
import type { AskUserQuestionTranscriptModel } from "../ask-user-question";
import { ToolPhaseGroup } from "./tool-phase-group";

function getAnswerText(
  model: Extract<AskUserQuestionTranscriptModel, { status: "answered" }>,
  questionId: string
) {
  return model.answers.find((answer) => answer.question_id === questionId)
    ?.answer_text;
}

export function AskUserQuestionSummary({
  model,
}: {
  model: AskUserQuestionTranscriptModel;
}) {
  const isPending = model.status === "pending";
  const isAnswered = model.status === "answered";
  const answeredModel = isAnswered ? model : null;
  const summaryLabel = isPending
    ? "Waiting for answer"
    : isAnswered
      ? "Answered"
      : "Declined";
  const testId = isPending
    ? "ask-user-question-pending"
    : isAnswered
      ? "ask-user-question-resolved"
      : "ask-user-question-declined";
  const trigger = ({ isOpen }: { isOpen: boolean }) => (
    <button
      type="button"
      aria-busy={isPending || undefined}
      className="group inline-flex max-w-full items-start justify-start gap-2 self-start text-left text-sm text-foreground/70 transition-colors hover:text-foreground"
      data-testid={isPending ? testId : undefined}
    >
      <span className="min-w-0">
        <span className="font-medium">
          {isPending ? <Shimmer>Question</Shimmer> : "Question"}
        </span>{" "}
        <span className="font-normal text-foreground/50 transition-colors group-hover:text-foreground">
          {summaryLabel}
        </span>
      </span>
      <ChevronDownIcon
        className={cn(
          "mt-0.5 size-4 shrink-0 opacity-0 transition-[opacity,transform] group-hover:opacity-100",
          isOpen && "rotate-180"
        )}
      />
    </button>
  );

  if (isPending) {
    return (
      <ToolPhaseGroup defaultOpen={false} renderTrigger={trigger}>
        {null}
      </ToolPhaseGroup>
    );
  }

  return (
    <ToolPhaseGroup defaultOpen={false} renderTrigger={trigger}>
      <div className="flex max-w-full flex-col gap-3" data-testid={testId}>
        {model.questions.map((question, index) => {
          const answerText = answeredModel
            ? (getAnswerText(answeredModel, question.id) ??
              "No answer recorded.")
            : "Dismissed by user.";

          return (
            <div
              key={question.id}
              className="flex max-w-full flex-col gap-1 text-sm"
            >
              <div className="font-medium text-foreground">
                {model.questions.length > 1 ? `${index + 1}. ` : null}
                {question.question}
              </div>

              <div className="text-muted-foreground">
                {isPending ? (
                  <Shimmer>Waiting for your input</Shimmer>
                ) : (
                  answerText
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ToolPhaseGroup>
  );
}
